import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card } from '@erp/ui';
import { ArrowRight } from '@/components/icons';
import { useSalesDocuments } from '@/hooks/use-sales';
import { useClients } from '@/hooks/use-clients';
import {
  DEFAULT_PERIOD_MODE,
  getComparisonRanges,
  inRange,
  type PeriodMode,
} from './period-range';

function formatCurrency(n: number): string {
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatPct(delta: number, base: number): string {
  if (base === 0) return delta === 0 ? '0%' : '+∞';
  const pct = (delta / base) * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(0)}%`;
}

const VS_KEY: Record<PeriodMode, string> = {
  'day-vs-yesterday': 'dashboard.compare.vs.yesterday',
  'day-vs-last-year': 'dashboard.compare.vs.sameDayLastYear',
  'week-vs-last-week': 'dashboard.compare.vs.lastWeek',
  'week-vs-last-year': 'dashboard.compare.vs.sameWeekLastYear',
  'month-vs-last-month': 'dashboard.compare.vs.lastMonth',
  'month-vs-last-year': 'dashboard.compare.vs.sameMonthLastYear',
  'year-vs-last-year': 'dashboard.compare.vs.sameYtdLastYear',
};

interface KpiProps {
  label: string;
  value: string;
  unit?: string;
  delta: number;
  base: number;
  positiveIsGood?: boolean;
  href: string;
  ctaLabel: string;
}

function Kpi({
  label,
  value,
  unit,
  delta,
  base,
  positiveIsGood = true,
  href,
  ctaLabel,
}: KpiProps) {
  const up = delta > 0;
  const isPositive = positiveIsGood ? up : delta < 0;
  const badgeStyle =
    delta === 0
      ? 'bg-muted/60 text-muted-foreground'
      : isPositive
        ? 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-400'
        : 'bg-rose-500/12 text-rose-700 dark:text-rose-400';

  return (
    <Card className="@container/card">
      <div className="flex flex-col gap-5 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="truncate text-[0.85rem] font-medium text-muted-foreground">{label}</div>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[0.7rem] font-semibold ${badgeStyle}`}
          >
            {formatPct(delta, base)}
          </span>
        </div>

        <div className="flex items-baseline gap-1.5">
          <span className="text-[1.9rem] font-bold leading-none tracking-tight tabular-nums @[260px]/card:text-[2.1rem]">
            {value}
          </span>
          {unit && (
            <span className="text-base font-medium text-muted-foreground">{unit}</span>
          )}
        </div>

        <Link
          to={href}
          className="group/cta inline-flex items-center gap-1 text-sm font-semibold text-[var(--primary)] transition-colors hover:text-[color-mix(in_oklch,var(--primary)_80%,black)]"
        >
          {ctaLabel}
          <ArrowRight size={14} className="transition-transform group-hover/cta:translate-x-0.5" />
        </Link>
      </div>
    </Card>
  );
}

function GoalCard({ pct, label }: { pct: number; label: string }) {
  const clamped = Math.max(0, Math.min(100, pct));
  const radius = 56;
  const stroke = 10;
  const circumference = Math.PI * radius;
  const dashOffset = circumference - (clamped / 100) * circumference;
  return (
    <Card className="@container/card">
      <div className="flex h-full flex-col items-center justify-between gap-4 p-5">
        <div className="self-stretch truncate text-center text-[0.85rem] font-medium text-muted-foreground">
          {label}
        </div>
        <div className="relative">
          <svg viewBox="0 0 140 80" className="h-20 w-32 overflow-visible">
            <path
              d={`M 14 70 A ${radius} ${radius} 0 0 1 126 70`}
              fill="none"
              stroke="color-mix(in oklch, var(--primary) 14%, var(--card))"
              strokeWidth={stroke}
              strokeLinecap="round"
            />
            <path
              d={`M 14 70 A ${radius} ${radius} 0 0 1 126 70`}
              fill="none"
              stroke="var(--primary)"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              style={{ transition: 'stroke-dashoffset 600ms ease-out' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-end justify-center pb-1">
            <span className="text-[1.7rem] font-bold leading-none tracking-tight tabular-nums">
              {clamped}%
            </span>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">de l’objectif mensuel</div>
      </div>
    </Card>
  );
}

interface SectionCardsProps {
  periodMode?: PeriodMode;
}

export function SectionCards({ periodMode = DEFAULT_PERIOD_MODE }: SectionCardsProps) {
  const { t } = useTranslation();
  const { data: invoices } = useSalesDocuments({ docType: 'invoice' });
  const { data: deliveries } = useSalesDocuments({ docType: 'delivery' });
  const { data: clients } = useClients();

  const stats = useMemo(() => {
    const { current, previous } = getComparisonRanges(periodMode);

    const revThis = (invoices ?? []).filter((d) => inRange(d.docDate, current)).reduce((s, d) => s + d.totalTtc, 0);
    const revLast = (invoices ?? []).filter((d) => inRange(d.docDate, previous)).reduce((s, d) => s + d.totalTtc, 0);

    const uninvoicedTotal = (deliveries ?? []).filter((d) => !d.isInvoiced).reduce((s, d) => s + d.totalTtc, 0);
    const uninvoicedThis = (deliveries ?? []).filter((d) => !d.isInvoiced && inRange(d.docDate, current)).reduce((s, d) => s + d.totalTtc, 0);
    const uninvoicedLast = (deliveries ?? []).filter((d) => !d.isInvoiced && inRange(d.docDate, previous)).reduce((s, d) => s + d.totalTtc, 0);

    // Goal: simple ratio of current revenue against a target. The target
    // here is a 20% growth over previous-period revenue (falls back to a
    // sensible floor for empty histories). Real goals can override this
    // later when a settings field for monthly target lands.
    const goalTarget = Math.max(revLast * 1.2, 10000);
    const goalPct = Math.round((revThis / goalTarget) * 100);

    return {
      revThis, revLast,
      uninvoicedTotal, uninvoicedThis, uninvoicedLast,
      goalPct,
    };
  }, [invoices, deliveries, clients, periodMode]);

  // vsLabel kept for future ("vs Avr. 2025") subtitle — currently unused but
  // exposed in case a designer wants to wire it back into the badge tooltip.
  void t(VS_KEY[periodMode]);

  return (
    <div className="grid grid-cols-1 gap-4 px-4 lg:px-6 @xl/main:grid-cols-2 @5xl/main:grid-cols-3">
      <Kpi
        label={t('dashboard.monthlyRevenue')}
        value={formatCurrency(stats.revThis)}
        unit="DT"
        delta={stats.revThis - stats.revLast}
        base={stats.revLast}
        href="/sales/invoices"
        ctaLabel={t('dashboard.viewDetails', { defaultValue: 'Voir détails' })}
      />
      <Kpi
        label={t('dashboard.outstanding')}
        value={formatCurrency(stats.uninvoicedTotal)}
        unit="DT"
        delta={stats.uninvoicedThis - stats.uninvoicedLast}
        base={stats.uninvoicedLast}
        positiveIsGood={false}
        href="/sales/deliveries"
        ctaLabel={t('dashboard.viewDetails', { defaultValue: 'Voir détails' })}
      />
      <GoalCard
        pct={stats.goalPct}
        label={t('dashboard.monthlyGoal', { defaultValue: 'Objectif du mois' })}
      />
    </div>
  );
}
